// src/pages/About.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDynamicCSS } from "../hooks/DynamicCSSLoader";
import { NeuralNoise } from "../components/ui/neural-noise";



const About = () => {
  useDynamicCSS("about");

  const pageRef = useRef(null);

  const [theme, setTheme] = useState(
    document?.documentElement?.getAttribute("data-theme") || "dark"
  );

  // ✅ About-only body class (prevents white / background clashes)
  useEffect(() => {
    document.body.classList.add("abXBody");
    return () => document.body.classList.remove("abXBody");
  }, []);

  // sync theme changes
  useEffect(() => {
    const el = document.documentElement;
    const updateTheme = () => setTheme(el.getAttribute("data-theme") || "dark");
    updateTheme();

    const obs = new MutationObserver(() => updateTheme());
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  // ✅ Set scroll progress variable used by CSS (0..1)
  useEffect(() => {
    const setScrollVar = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const max = Math.max(1, doc.scrollHeight - window.innerHeight);
      const v = Math.min(1, Math.max(0, scrollTop / max));

      if (pageRef.current) {
        pageRef.current.style.setProperty("--abXScroll", v.toFixed(4));
      }
    };

    setScrollVar();
    window.addEventListener("scroll", setScrollVar, { passive: true });
    window.addEventListener("resize", setScrollVar);
    return () => {
      window.removeEventListener("scroll", setScrollVar);
      window.removeEventListener("resize", setScrollVar);
    };
  }, []);

  // ✅ Popup state (for video + image)
  const [popup, setPopup] = useState({
    open: false,
    type: "video", // "video" | "image"
    src: "",
    title: "",
    campus: "",
  });

  const openPopup = (item) => {
    setPopup({
      open: true,
      type: item.type,
      src: item.src,
      title: item.title,
      campus: item.campus || "",
    });
    document.body.style.overflow = "hidden";
  };

  const closePopup = () => {
    setPopup((p) => ({ ...p, open: false }));
    document.body.style.overflow = "";
  };

  // ✅ Cleanup overflow if user navigates away while popup open
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closePopup();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ✅ SUCCESS STORIES
  const stories = useMemo(
    () => [
      {
        name: "K. Trisha",
        role: "AI Engineer",
        company: "Inikola",
        metric: "₹ 7.9 LPA",
        quote:
          "Capstone + code reviews taught me production thinking — not just theory.",
        image: "trisha.png",
        tag: "Career Switch",
      },
      {
        name: "Afseen Sultana",
        role: "Cloud + ML Engineer",
        company: "Inikola",
        metric: "₹ 7.8 LPA",
        quote:
          "Cloud + ML projects were practical and job-focused. The roadmap kept me consistent.",
        image: "afseen.png",
        tag: "Cloud + ML",
      },
      {
        name: "Divya",
        role: "Data Analyst",
        company: "Inikola",
        metric: "Outcome",
        quote:
          "The structured roadmap and mentorship made learning consistent and interview-ready.",
        image: "divya.jpeg",
        tag: "Placement",
      },
      {
        name: "N Vinay",
        role: "Data Analyst",
        company: "Inikola",
        metric: "₹ 8.5 LPA",
        quote:
          "Mentorship + weekly reviews gave me clarity. Mock interviews made the real ones feel easy.",
        image: "vinay.jpeg",
        tag: "Placement",
      },
      {
        name: "Poorna Chand",
        role: "Cloud + ML Engineer",
        company: "Inikola",
        metric: "₹ 8 LPA",
        quote:
          "Cloud + ML projects were practical and job-focused. The roadmap kept me consistent.",
        image: "poorna.jpeg",
        tag: "Cloud + ML",
      },
            {
        name: "Divya sri",
        role: "ML Engineer",
        company: "Inikola",
        metric: "₹ 8 LPA",
        quote:
          "ML projects were practical and job-focused. The roadmap kept me consistent.",
        image: "divya.jpeg",
        tag: "ML",
      },
    ],
    []
  );

  const loopStories = useMemo(() => [...stories, ...stories], [stories]);

  const values = useMemo(
    () => [
      {
        icon: "🧭",
        title: "Clarity-first learning",
        desc: "A guided roadmap that removes confusion and keeps you consistent week after week.",
      },
      {
        icon: "🛠️",
        title: "Project-driven approach",
        desc: "You build real apps, pipelines, GenAI systems and portfolios — not just notes.",
      },
      {
        icon: "🤝",
        title: "Mentorship & reviews",
        desc: "Weekly check-ins, feedback loops, and improvements until your work is job-ready.",
      },
      {
        icon: "🚀",
        title: "Career outcomes",
        desc: "Resume, mock interviews, deployment mindset, and the confidence to crack interviews.",
      },
    ],
    []
  );

  const team = useMemo(
    () => [
      {
        name: "Shiwang Chandra",
        role: "Trainer",
        image: "Shiwang.jpeg",
        desc: "Hands-on mentoring with practical learning and guided execution.",
      },
      {
        name: "Rohan Roy",
        role: "Trainer",
        image: "Rohan.jpeg",
        desc: "Structured learning support with project-first clarity and consistency.",
      },
      {
        name: "Bilal Ahmed",
        role: "Trainer",
        image: "bilal.jpeg",
        desc: "Analytics-first mindset with practical dashboards, reporting and business insights.",
      },
      {
        name: "Rohith Vushagoni",
        role: "Trainer",
        image: "rohith.png",
        desc: "RAG, agents and deployment workflows — turning prototypes into real products.",
      },
      {
        name: "Vishakha Pandole",
        role: "Trainer",
        image: "vishakha.jpeg",
        desc: "GenAI workflows, practical learning and portfolio-focused execution.",
      },
      {
        name: "Vibek Kumar Rana",
        role: "Trainer",
        image: "vibek.png",
        desc: "Product + AI execution: building systems, mentoring learners and shipping outcomes.",
      },
    ],
    []
  );

  const workshopMoments = useMemo(
    () => [
      {
        type: "video",
        campus: "IARE",
        title: "IARE — Workshop Reel",
        sub: "Live session + hands-on vibe",
        src: "/assets/iarevideo.mp4",
        thumb: "/assets/iareimg.jpeg",
      },
      {
        type: "video",
        campus: "VNR",
        title: "VNR — Workshop Reel",
        sub: "Projects + student energy",
        src: "/assets/vnrreel.mp4",
        thumb: "/assets/vnrimg.png",
      },
      {
        type: "image",
        campus: "IARE",
        title: "IARE — Classroom Moment",
        sub: "Mentor-led guidance",
        src: "/assets/iare1.jpeg",
      },
      {
        type: "image",
        campus: "IARE",
        title: "IARE — Student Interaction",
        sub: "Q&A + demos",
        src: "/assets/iare2.jpg",
      },
      {
        type: "image",
        campus: "VNR",
        title: "VNR — Workshop Snap",
        sub: "Hands-on practice",
        src: "/assets/vnr-1.jpg?v=3",
      },
      {
        type: "image",
        campus: "VNR",
        title: "VNR — FacultyCollabration",
        sub: "Team",
        src: "/assets/vnr-2.jpg?v=3",
      },
      {
        type: "image",
        campus: "St. Mary",
        title: "St. Mary — Session Moment",
        sub: "Learning + mentorship",
        src: "/assets/stmary-1.png",
      },
      {
        type: "image",
        campus: "St. Mary",
        title: "St. Mary — Classroom",
        sub: "Live guidance + projects",
        src: "/assets/stmary-2.png",
      },
    ],
    []
  );

  const reels = useMemo(
    () => workshopMoments.filter((x) => x.type === "video"),
    [workshopMoments]
  );
  const photos = useMemo(
    () => workshopMoments.filter((x) => x.type === "image"),
    [workshopMoments]
  );

  const neuralColor =
    theme === "light" ? [0.14, 0.41, 0.92] : [1.0, 0.65, 0.12];

  return (
    <div className="abXPage" ref={pageRef}>
      {/* Background */}
      <div className="abXGlobalBg abXGlobalBg--scroll" aria-hidden="true">
        <div className="abXGradLayer abXGradLayer--a" />
        <div className="abXGradLayer abXGradLayer--b" />
        <div className="abXFlowLines" />
        <div className="abXNoise" />
        <div className="abXVignette" />
      </div>

      <NeuralNoise
        className="abXNeuralNoise"
        color={neuralColor}
        opacity={theme === "light" ? 0.1 : 0.16}
        speed={0.00012}
      />

      {/* HERO */}
      <section className="abXHero">
        <div className="abXContainer">
          <div className="abXHeroInner">
            <div className="abXOverline">
              <span className="abXOverlineDot" />
              <span className="abXOverlineText">ABOUT US</span>
            </div>

            <h1 className="abXH1">
              <br />
              From learning to earning —{" "}
              <span className="abXGrad">with real projects & mentorship.</span>
            </h1>

            <p className="abXLead">
              CraftingBrain is an EdTech company from Hyderabad focused on{" "}
              <strong>Data Science, AI, Machine Learning and GenAI</strong>. We
              combine structure, mentorship, and real projects so learners build
              portfolios that stand out — and skills that actually work in jobs.
            </p>

            <div className="abXActions">
              <a className="abXBtn abXBtnPrimary" href="/coursespage">
                Explore Programs <span aria-hidden="true">→</span>
              </a>
              <a className="abXBtn abXBtnGhost" href="/contact">
                Talk to Us
              </a>
            </div>

            <div className="abXBadges" aria-label="Quick highlights">
              <div className="abXBadge">
                <div className="abXBadgeTop">6 Months</div>
                <div className="abXBadgeSub">Structured Roadmap</div>
              </div>
              <div className="abXBadge">
                <div className="abXBadgeTop">10+</div>
                <div className="abXBadgeSub">Live Projects</div>
              </div>
              <div className="abXBadge">
                <div className="abXBadgeTop">Weekly</div>
                <div className="abXBadgeSub">Mentorship & Reviews</div>
              </div>
              <div className="abXBadge">
                <div className="abXBadgeTop">Deploy</div>
                <div className="abXBadgeSub">Production Mindset</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="abXSection abXAlt">
        <div className="abXContainer">
          <div className="abXHead">
            <div className="abXKicker">What we stand for</div>
            <h2 className="abXH2">The CraftingBrain way</h2>
            <p className="abXSub">
              Structure + practice + mentorship + outcomes (no fluff).
            </p>
          </div>

          <div className="abXGrid4">
            {values.map((v) => (
              <div key={v.title} className="abXValue">
                <div className="abXValueIcon" aria-hidden="true">
                  {v.icon}
                </div>
                <div className="abXValueTitle">{v.title}</div>
                <div className="abXValueDesc">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES */}
      <section className="abXSection">
        <div className="abXContainer">
          <div className="abXHead">
            <div className="abXKicker">Outcomes</div>
            <h2 className="abXH2">Success stories</h2>
            <p className="abXSub">Real learners. Real journeys. Real results.</p>
          </div>

          <div className="abXMarquee" aria-label="Success stories carousel">
            <div className="abXTrack">
              {loopStories.map((s, idx) => (
                <article className="abXStory" key={`${s.name}-${idx}`}>
                  <div className="abXStoryTop">
                    <div className="abXAvatar">
                      <img src={s.image} alt={s.name} loading="lazy" />
                    </div>

                    <div className="abXMeta">
                      <div className="abXNameRow">
                        <div className="abXName">{s.name}</div>
                        <span className="abXChip">{s.tag}</span>
                      </div>
                      <div className="abXRole">{s.role}</div>
                      <div className="abXCompany">{s.company}</div>
                    </div>

                    <div className="abXMetric">
                      <div className="abXMetricLbl">Result</div>
                      <div className="abXMetricVal">{s.metric}</div>
                    </div>
                  </div>

                  <div className="abXQuote">“{s.quote}”</div>
                </article>
              ))}
            </div>
          </div>

          <div className="abXHint">Hover to pause • Swipe on mobile</div>
        </div>
      </section>

      {/* MOMENTS (REELS + PHOTOS SEPARATED) */}
      <section className="abXSection">
        <div className="abXContainer">
          <div className="abXHead">
            <div className="abXKicker">Highlights</div>
            <h2 className="abXH2">Moments from our Workshops</h2>
            <p className="abXSub">
              Quick reels + photos from IARE, VNR and St. Mary — real energy,
              real learning.
            </p>
          </div>

          <div className="abXMomentsSplit">
            {/* REELS */}
            <div className="abXMomentsBlock">
              <div className="abXMomentsBlockHead">
                <div className="abXMomentsTitleRow">
                  <h3 className="abXMomentsH3">Workshop Reels</h3>
                </div>
                <p className="abXMomentsDesc">
                  Tap to watch — best clips from live sessions & student energy.
                </p>
              </div>

              <div className="abXReelsGrid">
                {reels.map((item, i) => (
                  <button
                    key={`${item.title}-${i}`}
                    type="button"
                    className="abXMomentCard abXMomentCard--reel"
                    onClick={() => openPopup(item)}
                    aria-label={`Open reel: ${item.title}`}
                  >
                    <div className="abXMomentTop">
                      <span className="abXCampusPill">{item.campus}</span>
                      <span className="abXTypePill">Reel</span>
                    </div>

                    <div className="abXMomentMedia abXMomentMedia--reel">
                      <img
                        className="abXMomentThumb"
                        src={item.thumb}
                        alt={`${item.title} thumbnail`}
                        loading="lazy"
                      />
                      <div className="abXPlayBtn" aria-hidden="true">
                        <span className="abXPlayTri" />
                      </div>
                      <div className="abXMomentFade" aria-hidden="true" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* PHOTOS */}
            <div className="abXMomentsBlock">
              <div className="abXMomentsBlockHead">
                <div className="abXMomentsTitleRow">
                  <h3 className="abXMomentsH3">Photo Gallery</h3>
                </div>
                <p className="abXMomentsDesc">
                  Snapshots from classrooms, group activities, and mentor
                  guidance.
                </p>
              </div>

              <div className="abXPhotosGrid">
                {photos.map((item, i) => (
                  <button
                    key={`${item.title}-${i}`}
                    type="button"
                    className="abXMomentCard abXMomentCard--photo"
                    onClick={() => openPopup(item)}
                    aria-label={`Open photo: ${item.title}`}
                  >
                    <div className="abXMomentTop">
                      <span className="abXCampusPill">{item.campus}</span>
                      <span className="abXTypePill">Photo</span>
                    </div>

                    <div className="abXMomentMedia abXMomentMedia--photo">
                      <img
                        className="abXMomentImg"
                        src={item.src}
                        alt={item.title}
                        loading="lazy"
                      />
                      <div className="abXMomentFade" aria-hidden="true" />
                    </div>

                    <div className="abXMomentBody">
                      <div className="abXMomentTitle">{item.title}</div>
                      <div className="abXMomentSub">{item.sub}</div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="abXGalleryNote">
                Tap any photo to view full size • Swipe on mobile
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="abXSection">
        <div className="abXContainer">
          <div className="abXHead">
            <div className="abXKicker">People</div>
            <h2 className="abXH2">Meet the trainers</h2>
            <p className="abXSub">Mentors, builders and problem-solvers.</p>
          </div>

          <div className="abXTeamGrid4">
            {team.map((m) => (
              <article key={m.name} className="abXTeamCard abXTeamCard--circle">
                <div className="abXTeamAvatarWrap">
                  <div className="abXTeamAvatar">
                    <img src={m.image} alt={m.name} loading="lazy" />
                  </div>
                  <div className="abXTeamRolePill abXTeamRolePill--top">
                    {m.role}
                  </div>
                </div>

                <div className="abXTeamBody abXTeamBody--center">
                  <div className="abXTeamName">{m.name}</div>
                  <div className="abXTeamDesc">{m.desc}</div>

                  <div className="abXTeamFooter abXTeamFooter--center">
                    <span className="abXTeamDot" aria-hidden="true" />
                    <span className="abXTeamFooterText">
                      Trainer • Mentor • Reviewer
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="abXTrainerCountAfter">
            <span className="abXTrainerCountDot" aria-hidden="true" />
            <span className="abXTrainerCountText">10+ Expert Trainers</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="abXSection">
        <div className="abXContainer">
          <div className="abXCTA">
            <h2 className="abXH2">Want a roadmap that actually works?</h2>
            <p className="abXSub">
              Talk to our team — we’ll guide you based on your goal and current
              level.
            </p>

            <div className="abXActions abXCenter">
              <a className="abXBtn abXBtnPrimary" href="/coursespage">
                See Programs <span aria-hidden="true">→</span>
              </a>
              <a className="abXBtn abXBtnGhost" href="/contact">
                Request Callback
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* POPUP MODAL */}
      {popup.open && (
        <div
          className="abXModal"
          role="dialog"
          aria-modal="true"
          onMouseDown={closePopup}
        >
          <div
            className="abXModalCard"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              className="abXModalClose"
              onClick={closePopup}
              aria-label="Close"
            >
              ✕
            </button>

            <div className="abXModalHead">
              <div className="abXModalTitle">{popup.title}</div>
              {!!popup.campus && (
                <div className="abXModalMeta">{popup.campus}</div>
              )}
            </div>

            <div className="abXModalBody">
              {popup.type === "video" ? (
                <video
                  className="abXModalVideo"
                  controls
                  playsInline
                  autoPlay
                  preload="metadata"
                >
                  <source src={popup.src} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img className="abXModalImg" src={popup.src} alt={popup.title} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default About;